import { useState } from "react";
import { Form, Input, Spin } from "antd";
import { Link, useNavigate } from "react-router-dom";
import { LoadingOutlined } from "@ant-design/icons";
import { signUp } from "../../utils/api";

const ManagerRegister = () => {
	const [loading, setLoading] = useState(false);
	const [notMatchedError, setNotMatchedError] = useState("");

	const navigate = useNavigate();
	const [form] = Form.useForm();

	const onFinish = async (values) => {
		const cleanedValues = {
			password: values.password.trim(),
			email: values.email.trim(),
			phone: values.phone.trim(),
			name: values.name.trim(),
			role: "manager", // Hardcoded for manager registration
		};

		setLoading(true);
		setNotMatchedError("");

		try {
			const signUpRes = await signUp(cleanedValues);

			if (signUpRes?.response?.data?.statusCode === 400) {
				setNotMatchedError(signUpRes?.response?.data?.message);
				setLoading(false);
				return;
			}

			if (signUpRes?.data) {
				localStorage.setItem("token", signUpRes?.data?.data?.accessToken);
				if (signUpRes?.data?.data?.role) {
					localStorage.setItem("role", signUpRes?.data?.data?.role);
				}

				// Manager specific redirect
				navigate("/profile");
			}
		} catch (err) {
			console.log("error", err);
			setNotMatchedError("Something went wrong. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	const onFinishFailed = (errorInfo) => {
		console.log("Failed:", errorInfo);
	};

	return (
		<Spin
			spinning={loading}
			indicator={
				<LoadingOutlined
					style={{
						fontSize: 48,
						color: "white",
					}}
					spin
				/>
			}
		>
			<div className="relative min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 overflow-hidden">
				{/* Animated Background Blobs */}
				<div className="absolute inset-0 opacity-20">
					<div className="absolute top-0 -left-4 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
					<div className="absolute top-0 -right-4 w-72 h-72 bg-cyan-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
					<div className="absolute -bottom-8 left-20 w-72 h-72 bg-indigo-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
				</div>

				{/* Grid Pattern Overlay */}
				<div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>

				<div className="relative min-h-screen flex items-center justify-center px-4 py-12 pt-32">
					<div className="w-full max-w-md">
						<div className="bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 p-8 md:p-10 shadow-2xl">
							<div className="text-center mb-8">
								<div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-4">
									<span className="relative flex h-2 w-2">
										<span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
										<span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-500"></span>
									</span>
									<span className="text-sm text-gray-300">Manager Access</span>
								</div>
								<h1 className="text-3xl font-bold text-white mb-2">
									Manager Registration
								</h1>
								<p className="text-gray-400">Create your manager account</p>
							</div>

							<Form
								form={form}
								name="manager-register"
								onFinish={onFinish}
								onFinishFailed={onFinishFailed}
								autoComplete="off"
								layout="vertical"
								className="space-y-1"
							>
								<Form.Item
									name="name"
									rules={[
										{ required: true, message: "Please enter your name" },
									]}
								>
									<Input
										placeholder="Full name"
										className="w-full px-4 py-3.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:bg-white/20 focus:border-cyan-400 transition-all"
										style={{
											backgroundColor: "rgba(255,255,255,0.1)",
											color: "white",
										}}
									/>
								</Form.Item>

								<Form.Item
									name="email"
									rules={[
										{ required: true, message: "Please enter your email" },
										{ type: "email", message: "Please enter a valid email" },
									]}
								>
									<Input
										placeholder="Email address"
										className="w-full px-4 py-3.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:bg-white/20 focus:border-cyan-400 transition-all"
										style={{
											backgroundColor: "rgba(255,255,255,0.1)",
											color: "white",
										}}
									/>
								</Form.Item>

								<Form.Item
									name="password"
									rules={[
										{ required: true, message: "Please enter your password" },
										{
											min: 6,
											message: "Password must be at least 6 characters",
										},
									]}
								>
									<Input.Password
										placeholder="Password"
										className="w-full px-4 py-3.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus-within:bg-white/20 focus-within:border-cyan-400 transition-all [&>input]:bg-transparent [&>input]:text-white [&>input]:placeholder-gray-400 [&>input]:border-none [&>input]:focus:shadow-none"
										style={{
											backgroundColor: "rgba(255,255,255,0.1)",
											color: "white",
										}}
									/>
								</Form.Item>

								<Form.Item
									name="phone"
									rules={[
										{
											required: true,
											message: "Please enter your phone number",
										},
									]}
								>
									<Input
										placeholder="Phone number"
										type="tel"
										className="w-full px-4 py-3.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:bg-white/20 focus:border-cyan-400 transition-all"
										style={{
											backgroundColor: "rgba(255,255,255,0.1)",
											color: "white",
										}}
									/>
								</Form.Item>

								<Form.Item className="mb-2 mt-4">
									<button
										type="submit"
										disabled={loading}
										className={`w-full py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold rounded-xl shadow-lg hover:shadow-cyan-500/25 transition-all transform hover:-translate-y-0.5 active:translate-y-0 ${
											loading
												? "opacity-70 cursor-not-allowed"
												: "hover:from-cyan-600 hover:to-blue-700"
										}`}
									>
										Create Manager Account
									</button>
								</Form.Item>

								{notMatchedError && (
									<div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl">
										<p className="text-red-400 text-sm text-center font-medium">
											{notMatchedError}
										</p>
									</div>
								)}
							</Form>

							<div className="mt-8 pt-6 border-t border-white/10 text-center">
								<p className="text-gray-400">
									Already have an account?{" "}
									<Link
										to="/login"
										className="text-cyan-400 font-semibold hover:text-cyan-300 transition-colors"
									>
										Sign in
									</Link>
								</p>
							</div>
						</div>
					</div>
				</div>
			</div>
		</Spin>
	);
};

export default ManagerRegister;
